import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Car, ShowerHead, Dumbbell, MapPin } from "lucide-react";
import Seo, { breadcrumbSchema, faqSchema } from "../components/Seo";
import { Reveal, Eyebrow, PageHero, BtnPrimary, BtnGhost, FaqList, CtaBand, Crumbs } from "../components/Shared";
import { LeadBand } from "../components/LeadForm";
import { IMG, SITE, chalkitLink } from "../data/mock";

const dropinFaqs = [
  { q: "How much is a drop-in?", a: "$35 per class. Pay online through ChalkIt Pro or at the front desk. Staying longer? A 10-class punch card is $300 with a 3-month expiration." },
  { q: "How much is a week pass?", a: "A one-week drop-in pass at Boston Iron Grit is $75 and covers every class on the schedule for seven days." },
  { q: "Can a beginner drop in?", a: "Standard drop-ins are for athletes familiar with CrossFit. If you're new, book a free goal review instead — we'll take great care of you." },
  { q: "Do I need to book ahead?", a: "Yes — submit the visiting athlete form and wait for confirmation so we can guarantee your spot and equipment." },
  { q: "Is there parking?", a: "Yes, free on-site parking — a rarity for Boston. The entrance is on Sambuca Way Alley off VFW Parkway." },
  { q: "Can I use open gym while visiting?", a: "Visiting athletes can add open gym access (weekdays 10:30–11:30 AM — afternoon times coming soon) — just ask when you book." },
];

export default function DropIn() {
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Drop-In", path: "/drop-in" },
  ];

  return (
    <>
      <Seo
        title="CrossFit Drop-In Boston | Boston Iron Grit"
        description="Visiting Boston? Experienced CrossFit athletes can drop in at Boston Iron Grit in West Roxbury for $35 per class. Free parking and showers. Book ahead."
        path="/drop-in"
        schemas={[breadcrumbSchema(crumbs), faqSchema(dropinFaqs)]}
      />
      <PageHero
        label="Visiting Athletes"
        title={<>In town? Come get<br /><span className="text-neutral-500">your workout in.</span></>}
        sub="Experienced CrossFit athletes are always welcome. Book ahead, tell us about your training, and we'll have a spot (and a bar) waiting. Visitors call us one of the friendliest drop-ins in Boston."
        image={IMG.pullups}
      />

      <section className="bg-[#0A0A0B] pb-6">
        <div className="max-w-7xl mx-auto px-6">
          <Crumbs items={crumbs} />
        </div>
      </section>

      {/* Options + how it works */}
      <section className="bg-[#0A0A0B] py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14">
          <Reveal>
            <Eyebrow>Drop-In Options</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-3xl lg:text-4xl text-white tracking-tight leading-tight mb-8">Simple pricing</h2>
            <div className="space-y-5">
              <div className="relative p-7 pt-8 bg-[#121214] border-2 border-[#9D0423]" data-testid="dropin-week-pass-card">
                <span className="absolute -top-3 left-6 bg-[#9D0423] text-white font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1">Try Us for a Week</span>
                <div className="flex items-baseline justify-between">
                  <h3 className="font-heading font-bold uppercase text-xl text-white">Week Pass · 7 Days</h3>
                  <div className="font-heading font-extrabold text-4xl text-white">$75</div>
                </div>
                <p className="mt-3 text-[13px] text-neutral-400 leading-relaxed">
                  Every class on the schedule for seven days — CrossFit, HYROX, Olympic lifting, B.I.G. Engine and open gym.
                  Less than the cost of three single drop-ins.
                </p>
                <a
                  href={chalkitLink("dropInWeek")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="dropin-week-cta"
                  className="mt-5 inline-flex items-center justify-center gap-2 w-full sm:w-auto bg-[#9D0423] hover:bg-[#7E031C] text-white font-mono font-semibold text-[12px] uppercase tracking-[0.14em] px-6 py-3.5 transition-colors"
                >
                  Get the $75 Week Pass →
                </a>
              </div>
              <div className="p-7 bg-[#121214] border border-white/8">
                <div className="flex items-baseline justify-between">
                  <h3 className="font-heading font-bold uppercase text-xl text-white">Drop-In · 1x Class</h3>
                  <div className="font-heading font-extrabold text-4xl text-white">$35</div>
                </div>
                <p className="mt-3 text-[13px] text-neutral-400 leading-relaxed">One coached class. Great for a quick visit — grab a workout, meet the community, get on with your day.</p>
                <a
                  href={chalkitLink("dropIn")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="dropin-single-cta"
                  className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-[#E56A79] hover:text-white transition-colors"
                >
                  Book a Single Class →
                </a>
              </div>
              <div className="p-7 bg-[#121214] border border-white/8">
                <div className="flex items-baseline justify-between">
                  <h3 className="font-heading font-bold uppercase text-xl text-white">Staying Longer?</h3>
                  <div className="font-heading font-extrabold text-4xl text-white">$300</div>
                </div>
                <p className="mt-3 text-[13px] text-neutral-400 leading-relaxed">10-class punch card with a 3-month expiration. The extended-stay favorite.</p>
              </div>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <BtnPrimary href={chalkitLink("dropIn")}>Book via ChalkIt Pro</BtnPrimary>
              <BtnGhost to="/schedule">Check the Schedule</BtnGhost>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <Eyebrow>Before You Arrive</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-3xl lg:text-4xl text-white tracking-tight leading-tight mb-8">The game plan</h2>
            <ul className="space-y-4">
              {[
                "Book your class ahead — don't assume every class has space",
                "Tell us how long you've trained and your home affiliate",
                "Arrive 10–15 minutes early to meet your coach",
                "Scale appropriately — travel and jet lag are real",
                "Bring water, training clothes and your usual gear",
              ].map((s) => (
                <li key={s} className="flex items-start gap-3 text-[15px] text-neutral-300 leading-relaxed">
                  <CheckCircle2 size={17} className="text-[#9D0423] mt-0.5 shrink-0" /> {s}
                </li>
              ))}
            </ul>
            <div className="mt-8 grid grid-cols-3 gap-px bg-white/5 border border-white/5">
              {[
                { icon: Car, label: "Free Parking" },
                { icon: ShowerHead, label: "Showers" },
                { icon: Dumbbell, label: "Full Equipment" },
              ].map((f) => (
                <div key={f.label} className="bg-[#0D0D0F] p-5 text-center">
                  <f.icon size={19} className="text-[#9D0423] mx-auto" />
                  <div className="mt-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-400">{f.label}</div>
                </div>
              ))}
            </div>
            <div className="mt-6 flex items-start gap-3 p-5 bg-[#121214] border border-white/5">
              <MapPin size={16} className="text-[#9D0423] mt-0.5 shrink-0" />
              <p className="text-[13px] text-neutral-400 leading-relaxed">
                {SITE.address.full} — we're in West Roxbury, not downtown, so plan drive time with live traffic.
                Driving from Dedham, Brookline, Needham or Newton? You're 10–15 minutes away.{" "}
                <Link to="/blog/crossfit-drop-in-boston-guide" className="text-[#E56A79] hover:text-white transition-colors">Read the full visiting athlete guide →</Link>
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#0D0D0F] border-y border-white/5 py-20">
        <div className="max-w-4xl mx-auto px-6">
          <Reveal>
            <FaqList faqs={dropinFaqs} title="Drop-In — FAQs" />
          </Reveal>
        </div>
      </section>

      <LeadBand
        formName="drop-in"
        programInterest="Drop In / Visiting"
        title="Questions before you visit?"
        sub="Tell us when you're in town and what you train — a coach will reply with class recommendations and anything you need to know."
      />

      <CtaBand title="New to CrossFit?" sub="Drop-ins are for experienced athletes — but if you're new, we'd love to meet you properly. Book a free goal review and we'll build your on-ramp." />
    </>
  );
}
